import React, { useState, useRef, useEffect, useContext } from "react";
import { DeSoIdentityContext } from "react-deso-protocol";
import { deso_graphql } from "../utils/graphql";
import { createUserAssociation, deleteUserAssociation, getUserAssociations, identity, uploadImage } from "deso-protocol";
import { localFees, systemKey } from "..";
import { Alert, Button, Card, Col, Container, Dropdown, Form, InputGroup, Modal, Row, Spinner } from "react-bootstrap";
import { handleLogin, UserDropdown } from "../appLoader";
import { localCurrency } from "../utils/currency";
import { useUserPreferences } from "../contexts/UserPreferences";
import ExchangeRatesContext from "../contexts/ExchangeRatesContext";
import { AccessGroupsContext } from "../contexts/AccessGroups";
import { encryptString, formatBytes, generateUniqueId, getFileExtension, getFilenameWithoutExtension } from "./StorageHelpers";

const MAX_ASSOCIATION_SIZE = 25000;
const BUFFER_SIZE = 200;
const associationType = "IMAGE_TEST";
const MAX_UPLOAD_WIDTH = 1500;
const sizeMap = {
    full: null,
    large: 5000,
    medium: 2500,
    default: 1500,
    small: 1000,
    tiny: 500,
};
const LOCAL_FEE = 0.0001;
const ASSOCIATION_COST = 0.000149758 + LOCAL_FEE;

export const Upload = ({ uploadModal, setUploadModal, setRefresh }) => {
    const { currentUser, isLoading } = useContext(DeSoIdentityContext);
    const { accessGroups } = useContext(AccessGroupsContext);
    const { preferences } = useUserPreferences();
    const { exchangeRates } = useContext(ExchangeRatesContext);
    console.log(accessGroups);
    const fileInputRef = useRef();

    const [processing, setProcessing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(null);
    const [uploadError, setUploadError] = useState(null);


    //const [uploadModal, setUploadModal] = useState(false);
    const [uploadFileData, setUploadFileData] = useState(null);

    const triggerFileInput = () => fileInputRef.current?.click();

    const [encryptionOption, setEncryptionOption] = useState(null);

    const [resizeOption, setResizeOption] = useState('default');
    const [compressionQuality, setCompressionQuality] = useState(0.85);
    const [imageFormat, setImageFormat] = useState('webp');

    const [filename, setFilename] = useState('');
    const [filenameError, setFilenameError] = useState(null);

    const [estimatedChunks, setEstimatedChunks] = useState(null);
    const [estimatedCost, setEstimatedCost] = useState(null);

    const [indexOption, setIndexOption] = useState(0);

    useEffect(() => {
        if (!uploadFileData) {
            //console.log("No uploadFileData available");
            return;
        }

        const estimateChunks = async () => {
            setProcessing(true);
            //console.log("Estimating chunks for", uploadFileData);

            let totalFileSize = 0; // Total size of the file in bytes
            let estimatedChunks = 0;

            if (uploadFileData.isImage) {
                const fileType = uploadFileData.file.type.toLowerCase();
                const isGif = fileType === 'image/gif';

                if (isGif) {
                    // Handle GIF
                    const gifSize = uploadFileData.blob.size;
                    const chunkSize = MAX_ASSOCIATION_SIZE - calculateOverhead("https://placeholder.com/gif"); // Mock URL
                    estimatedChunks = Math.ceil(gifSize / chunkSize);
                    setEstimatedChunks(estimatedChunks);
                    setEstimatedCost(estimatedChunks * ASSOCIATION_COST);
                    //console.log("Estimated chunks: " + estimatedChunks + ", Estimated cost: " + (estimatedChunks * ASSOCIATION_COST));
                } else {
                    // For other image formats (like JPEG, PNG, WebP), apply resizing
                    const img = new Image();
                    img.onload = async () => {
                        const canvas = document.createElement("canvas");
                        const ctx = canvas.getContext("2d");

                        // Apply resizing based on the sizeMap
                        const { w, h } = sizeMap[resizeOption]
                            ? {
                                w: Math.min(sizeMap[resizeOption], img.width),
                                h: (img.height * Math.min(sizeMap[resizeOption], img.width)) / img.width,
                            }
                            : { w: img.width, h: img.height };

                        canvas.width = w;
                        canvas.height = h;
                        ctx.drawImage(img, 0, 0, w, h);

                        // Create Blob from the canvas with the selected format and compression
                        const resizedBlob = await new Promise((resolve) => {
                            canvas.toBlob(resolve, `image/${imageFormat}`, compressionQuality);
                        });

                        // Calculate overhead
                        const overhead = calculateOverhead("https://placeholder.com/image.webp"); // Recalculate overhead based on format
                        const chunkSize = MAX_ASSOCIATION_SIZE - overhead - BUFFER_SIZE;

                        // **Base64 Encoding Simulation**: Adjust the size of the blob for base64 encoding
                        const base64Size = resizedBlob.size * 1.33; // Account for 33% increase due to base64 encoding
                        estimatedChunks = Math.ceil(base64Size / chunkSize);

                        setEstimatedChunks(estimatedChunks);
                        setEstimatedCost(estimatedChunks * ASSOCIATION_COST); // Estimate cost for image files
                        setProcessing(false);
                        //console.log("Estimated chunks: " + estimatedChunks + ", Estimated cost: " + (estimatedChunks * ASSOCIATION_COST));
                    };

                    const objectUrl = URL.createObjectURL(uploadFileData.blob);
                    img.src = objectUrl;
                }
            } else {
                // Handle non-image files (e.g., PDFs, DOCX, etc.)
                const fileSize = uploadFileData.blob.size;

                // **Base64 Encoding Simulation**: Adjust the size of the file for base64 encoding
                const base64Size = fileSize * 1.33; // Account for 33% increase due to base64 encoding
                const chunkSize = MAX_ASSOCIATION_SIZE - calculateOverhead("https://placeholder.com/file"); // Mock URL
                estimatedChunks = Math.ceil(base64Size / chunkSize);

                setEstimatedChunks(estimatedChunks);
                setEstimatedCost(estimatedChunks * ASSOCIATION_COST); // Estimate cost for non-image files
                setProcessing(false);
                //console.log("Estimated chunks: " + estimatedChunks + ", Estimated cost: " + (estimatedChunks * ASSOCIATION_COST));
            }
        };

        estimateChunks();
    }, [resizeOption, compressionQuality, uploadFileData, imageFormat]);




    const calculateOverhead = (imageUrl) => {
        const associationRequest = {
            AppPublicKeyBase58Check: systemKey,
            TargetUserPublicKeyBase58Check: currentUser?.PublicKeyBase58Check,
            TransactorPublicKeyBase58Check: currentUser?.PublicKeyBase58Check,
            AssociationType: associationType,
            AssociationValue: imageUrl + '_9999',
            ExtraData: {
                ImageURL: imageUrl,
                Parts: "9999",
                timeStamp: Date.now().toString(),
                content: "",
                filename: "abcdefghijklmnopqrstuvwxyz1234567890.webp",
                filetype: "image/webp",
                extension: "webp",
            },
            TransactionFees: [localFees()],
        };
        const totalLength = JSON.stringify(associationRequest).length;
        return totalLength + BUFFER_SIZE;
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !currentUser) return;
        setProcessing(true);

        const fileType = file.type;
        const isImage = fileType.startsWith("image/");

        if (isImage) {
            const imgURL = URL.createObjectURL(file);
            const img = new Image();

            img.onload = async () => {
                // Full-size image blob
                const fullCanvas = document.createElement("canvas");
                fullCanvas.width = img.width;
                fullCanvas.height = img.height;
                fullCanvas.getContext("2d").drawImage(img, 0, 0);
                const fullBlob = await new Promise(resolve =>
                    fullCanvas.toBlob(resolve, "image/webp", compressionQuality)
                );

                // Resize image for upload
                let resizedBlob = fullBlob;
                let resizedWidth = img.width;
                if (img.width > MAX_UPLOAD_WIDTH) {
                    const scale = MAX_UPLOAD_WIDTH / img.width;
                    const uploadCanvas = document.createElement("canvas");
                    uploadCanvas.width = MAX_UPLOAD_WIDTH;
                    uploadCanvas.height = img.height * scale;
                    const ctx = uploadCanvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, uploadCanvas.width, uploadCanvas.height);
                    resizedBlob = await new Promise(resolve =>
                        uploadCanvas.toBlob(resolve, "image/webp", 0.8)
                    );
                    resizedWidth = MAX_UPLOAD_WIDTH;
                }

                const uploadFile = new File([resizedBlob], file.name, { type: "image/webp" });

                setUploadFileData({
                    file,
                    type: fileType,
                    isImage,
                    blob: fullBlob,
                    uploadFile,
                    width: img.width,
                    height: img.height,
                    extension: getFileExtension(file.name),
                });
                setFilename(getFilenameWithoutExtension(file.name));
                URL.revokeObjectURL(imgURL);
            };

            img.src = imgURL;
        } else {
            // Other files - don't read base64, store blob directly
            setUploadFileData({
                file,
                type: fileType,
                isImage: false,
                blob: file,
                size: file.size,
                extension: getFileExtension(file.name),
            });
            setFilename(getFilenameWithoutExtension(file.name));
        }
    };


    function blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const binary = new Uint8Array(reader.result);
                let binaryStr = "";
                for (let i = 0; i < binary.length; i++) {
                    binaryStr += String.fromCharCode(binary[i]);
                }
                resolve(btoa(binaryStr)); // No MIME prefix
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(blob);
        });
    }

    const handleFileUpload = async () => {
        if (!uploadFileData || !currentUser) return;

        let AssociationValuePrefix = generateUniqueId();

        const { file, blob, width, height, isImage } = uploadFileData;
        const publicKey = currentUser.PublicKeyBase58Check;

        setUploading(true);
        setUploadError(null);
        setProgress(0);

        try {
            // Image-specific upload logic
            if (isImage) {
                let imageUrl;
                if (uploadFileData.uploadFile) {

                    // Step 1: Upload full image to get URL (needed for reference)
                    // used as a quickly accessible version of the image
                    imageUrl = await uploadImage({
                        UserPublicKeyBase58Check: publicKey,
                        file: uploadFileData.uploadFile,
                    }, { nodeURI: 'https://node.deso.org' });

                    if (!imageUrl?.ImageURL) {
                        setUploading(false);
                        return;
                    }

                    const baseImageURL = imageUrl.ImageURL;

                    // Step 2: Resize and compress the image if needed
                    let processedBlob = blob; // original full image
                    if (resizeOption !== 'full') {
                        const scale = sizeMap[resizeOption] ? sizeMap[resizeOption] / width : 1;
                        if (scale < 1) {
                            const canvas = document.createElement("canvas");
                            const ctx = canvas.getContext("2d");
                            canvas.width = Math.round(width * scale);
                            canvas.height = Math.round(height * scale);

                            const img = new Image();
                            img.src = baseImageURL;

                            await new Promise((resolve) => {
                                img.onload = () => {
                                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                                    canvas.toBlob(
                                        (resizedBlob) => {
                                            processedBlob = resizedBlob;
                                            resolve();
                                        },
                                        `image/${imageFormat}`,
                                        compressionQuality
                                    );
                                };
                            });
                        }
                    }

                    // Step 3: Convert full blob to base64
                    const base64raw = await blobToBase64(processedBlob);

                    ///////////////////////////////////////////////////////
                    ///// DEALING WITH BASE64 FROM THIS POINT ONWARDS /////
                    ///////////////////////////////////////////////////////
                    // Step 4: Calculate overhead and chunk size
                    const overhead = calculateOverhead(AssociationValuePrefix);
                    const base64AdjustedChunkSize = Math.floor((MAX_ASSOCIATION_SIZE - overhead) * 0.75); // base64 overhead ~33%

                    const chunks = [];
                    let cursor = 0;
                    
                    if (encryptionOption) {
                        const firstChunkSize = 750; // encryption increases chunked size by 20-30x and we're now including more header information
                        const firstChunkRaw = base64raw.slice(0, firstChunkSize);
                    
                        const encryptedFirstChunk = await encryptString(firstChunkRaw, encryptionOption, accessGroups);
                        console.log("Encrypted first chunk:", encryptedFirstChunk);
                        chunks.push(encryptedFirstChunk);
                    
                        cursor = firstChunkSize; // Move cursor past the first encrypted chunk
                    }
                    
                    // Step 1: Process remaining (or all) chunks normally
                    for (let i = cursor; i < base64raw.length; i += base64AdjustedChunkSize) {
                        chunks.push(base64raw.slice(i, i + base64AdjustedChunkSize));
                    }

                    console.log("Chunks:", chunks.length, chunks);

                
                    // Prepare base request
                    const totalBytes = base64raw.length;
                    let uploadedBytes = 0;

                    let baseRequest = {
                        AppPublicKeyBase58Check: systemKey,
                        TargetUserPublicKeyBase58Check: publicKey,
                        TransactorPublicKeyBase58Check: publicKey,
                        AssociationType: associationType,
                        TransactionFees: [localFees()],
                    };

                    let headerRequest = {};

                    let fileData = {
                        ImageURL: baseImageURL,
                        Parts: chunks.length.toString(),
                        timeStamp: Date.now().toString(),
                        filename: (filename + "." + imageFormat) || file.name,
                        filetype: file.type,
                        extension: imageFormat,
                    }

                    if (encryptionOption) {
                        const { AccessGroupsOwned = [], AccessGroupsMember = [] } = accessGroups;
                        const allAccessGroups = [...AccessGroupsOwned, ...AccessGroupsMember];

                        const accessGroupKey = allAccessGroups.find(
                            (group) => group.AccessGroupKeyName === encryptionOption
                        )?.AccessGroupPublicKeyBase58Check;

                        headerRequest = {
                            ExtraData: {
                                fileData: await encryptString(JSON.stringify(fileData), encryptionOption, accessGroups),
                                accessGroup: accessGroupKey,
                            }
                        }
                    } else {
                        headerRequest = {
                            ExtraData: {
                                ...fileData,
                            }
                        }
                    }

                    // Step 6: Upload each chunk
                    for (let i = 0; i < chunks.length; i++) {
                        let associationRequest = {};

                        if(i === 0) {
                            // First chunk with header
                            associationRequest = {
                                ...baseRequest,
                                AssociationValue: `${AssociationValuePrefix}_${i}`,
                                ExtraData: {
                                    ...headerRequest.ExtraData,
                                    content: chunks[i],
                                },
                            }
                        } else {
                            associationRequest = {
                                ...baseRequest,
                                AssociationValue: `${AssociationValuePrefix}_${i}`,
                                ExtraData: {
                                    content: chunks[i],
                                },
                            };
                        }

                        console.log("Ass["+i+"]:",associationRequest);
                        //await createUserAssociation(associationRequest);

                        uploadedBytes += chunks[i].length;
                        setProgress((uploadedBytes / totalBytes) * 100);
                    }

                    // Final cleanup
                    setUploadError(null);
                    setProgress(null);
                    setUploading(false);
                    setUploadFileData(null);
                    setRefresh(true);
                    setUploadModal(false);
                }
            } else {
                // Handle non-image files (e.g., PDFs, DOCX, etc.)
                const blob = uploadFileData.blob; // your actual Blob object
                const overhead = calculateOverhead(AssociationValuePrefix);

                // Calculate base64 size increase (approximately 33% larger than the original blob)
                const base64Size = Math.ceil(blob.size * 4 / 3);  // Calculate base64 size from blob size

                // Adjust chunk size based on the base64 size
                const base64AdjustedChunkSize = Math.floor((MAX_ASSOCIATION_SIZE - overhead - BUFFER_SIZE) * (3 / 4));

                const chunks = [];
                let start = 0;
                let base64Raw = '';

                try {
                    // Convert Blob to base64 using a promise to handle FileReader async behavior
                    base64Raw = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const result = reader.result.split(',')[1]; // Get the base64 data from the result
                            resolve(result);
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });

                    // Split the base64 data into chunks based on the adjusted chunk size
                    while (start < base64Raw.length) {
                        const availableChunkSize = Math.min(base64AdjustedChunkSize, base64Raw.length - start);
                        if (availableChunkSize > 0) {
                            chunks.push(base64Raw.slice(start, start + availableChunkSize));
                            start += availableChunkSize;
                        }
                    }
                } catch (err) {
                    console.error("Error converting blob to base64:", err);
                    setUploading(false);
                    return;
                }

                ///////////////////////////////////////////////////////
                ///// DEALING WITH BASE64 FROM THIS POINT ONWARDS /////
                ///////////////////////////////////////////////////////
                const totalBytes = base64Raw.length;  // The size of the base64 data, not the blob
                let uploadedBytes = 0;
                const associationRequest = {
                    AppPublicKeyBase58Check: systemKey,
                    TargetUserPublicKeyBase58Check: publicKey,
                    TransactorPublicKeyBase58Check: publicKey,
                    AssociationType: associationType,
                    ExtraData: {
                        Parts: chunks.length.toString(),
                        timeStamp: Date.now().toString(),
                        filename: (filename + "." + file.extension) || file.name,
                        filetype: file.type,
                        extension: file.name.includes('.') ? file.name.split('.').pop() : null,
                    },
                    TransactionFees: [localFees()],
                };

                // Process each chunk and send the request
                for (let i = 0; i < chunks.length; i++) {
                    try {
                        // Convert base64 chunk to appropriate format
                        associationRequest.ExtraData.content = chunks[i];
                        associationRequest.AssociationValue = `${AssociationValuePrefix}_${i}`;
                        await createUserAssociation(associationRequest);
                    } catch (err) {
                        console.error("Error uploading chunk:", err);
                        setUploading(false);
                        setUploadError("Sorry, your upload to blockchain failed, please try again.");
                        return;
                    }

                    uploadedBytes += chunks[i].length;
                    setProgress((uploadedBytes / totalBytes) * 100);
                }
                setUploadError(null);
                setProgress(null);
                setUploading(false);
                setRefresh(true);
                setUploadModal(false);
            }
        } catch (err) {
            console.error("Error during file upload:", err);
            setUploading(false);
            setUploadError("Sorry, your upload to blockchain failed, please try again.");
        }
    };





    /* original working
    const handleImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !currentUser) return;
        setUploading(true);
        setProgress(0);

        const reader = new FileReader();
        const publicKey = currentUser?.PublicKeyBase58Check;

        reader.onloadend = async () => {
            const img = new Image();
            img.onload = async () => {
                // standard upload - resizing the image if necessary
                const uploadCanvas = document.createElement("canvas");
                const uploadCtx = uploadCanvas.getContext("2d");
                if (img.width > MAX_UPLOAD_WIDTH) {
                    const scale = MAX_UPLOAD_WIDTH / img.width;
                    uploadCanvas.width = MAX_UPLOAD_WIDTH;
                    uploadCanvas.height = img.height * scale;
                } else {
                    uploadCanvas.width = img.width;
                    uploadCanvas.height = img.height;
                }
                uploadCtx.drawImage(img, 0, 0, uploadCanvas.width, uploadCanvas.height);
                const uploadBlob = await new Promise(resolve => uploadCanvas.toBlob(resolve, "image/webp", 0.8));
                const uploadFile = new File([uploadBlob], file.name, { type: "image/webp" });
                const imageUrl = await uploadImage({
                    UserPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
                    file: uploadFile,
                }, { nodeURI: 'https://node.deso.org' });
                if (!imageUrl?.ImageURL) return;

                const baseImageURL = imageUrl.ImageURL;

                // Creating the on-chain backup
                const backupCanvas = document.createElement("canvas");
                backupCanvas.width = img.width;
                backupCanvas.height = img.height;
                const backupCtx = backupCanvas.getContext("2d");
                backupCtx.drawImage(img, 0, 0);
                const base64Webp = backupCanvas.toDataURL("image/webp", 0.8);
                const chunks = [];

                // Calculate overhead once outside of the loop
                const overhead = calculateOverhead(imageUrl.ImageURL);

                let start = 0;
                while (start < base64Webp.length) {
                    // Adjust available chunk size
                    const availableChunkSize = Math.min(MAX_ASSOCIATION_SIZE - overhead - BUFFER_SIZE, base64Webp.length - start);
                    //console.log("Calculated available chunk size [" + start + "]: " + availableChunkSize);

                    if (availableChunkSize > 0) {
                        chunks.push(base64Webp.slice(start, start + availableChunkSize));
                        start += availableChunkSize; // Move the start pointer
                    }
                }

                // Store each chunk in associations directly without state update
                let uploadedBytes = 0;
                const totalBytes = base64Webp.length;

                const associationRequest = {
                    AppPublicKeyBase58Check: systemKey,
                    TargetUserPublicKeyBase58Check: publicKey,
                    TransactorPublicKeyBase58Check: publicKey,
                    AssociationType: associationType,
                    ExtraData: {
                        ImageURL: baseImageURL,
                        Parts: chunks.length.toString(),
                        timeStamp: Date.now().toString(),
                    },
                    TransactionFees: [localFees()],
                };
                
                for (let i = 0; i < chunks.length; i++) {
                    associationRequest.AssociationValue = `${baseImageURL}_${i}`;
                    associationRequest.ExtraData.content = chunks[i];
                    await createUserAssociation(associationRequest);
                    uploadedBytes += chunks[i].length;
                    const progressPercentage = (uploadedBytes / totalBytes)*100;
                    setProgress(progressPercentage);
                }

                //alert("Image saved and backed up on-chain.");
                setProgress(null);
                setUploading(false);
                setRefresh(true);
            };

            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    };
    */

    const handleFilenameChange = async (e) => {
        const value = e.target.value;  // Capture the raw value from the input

        setFilename(value); // Set the value as the user types

        // Debounce the filename validation and uniqueness check
        const delayDebounceFn = setTimeout(async () => {
            // Trim whitespace and validate the filename
            const trimmedValue = value.trim();

            // Validate allowed characters (letters, numbers, spaces, dots, dashes, underscores)
            const isValid = /^[a-zA-Z0-9\s._-]*$/.test(trimmedValue);

            if (!isValid) {
                setFilenameError("Filename cannot contain special characters");
                return;
            }

            // Clear error if filename is valid
            setFilenameError(null);

            // Only check if filename is not empty after trimming
            if (trimmedValue !== "") {
                try {
                    // Check if the filename exists by calling the API with the trimmed value
                    const data = await getUserAssociations({
                        TransactorPublicKeyBase58Check: currentUser?.PublicKeyBase58Check,
                        TargetUserPublicKeyBase58Check: currentUser?.PublicKeyBase58Check,
                        AssociationValue: trimmedValue + "_0",
                        AssociationType: associationType,
                    });

                    if (data.Associations.length > 0) {
                        setFilenameError("A file with this name already exists.");
                    } else {
                        setFilenameError(null); // Clear error if filename is unique
                    }
                } catch (error) {
                    console.error("Error checking filename:", error);
                    setFilenameError("There was an error checking the filename.");
                }
            }
        }, 500); // Debounce delay (500ms)

        // Cleanup previous timeout if the filename changes before the timeout
        return () => clearTimeout(delayDebounceFn);
    };

    useEffect(() => {
        if (filename.trim() === "") {
            setFilenameError(null); // Reset error if filename is empty (trimmed)
        }
    }, [filename]);

    useEffect(() => {
        if (encryptionOption && accessGroups?.AccessGroupsMember) {
            const selectedAccessGroup = accessGroups.AccessGroupsMember.find(group => group.AccessGroupKeyName === encryptionOption);
            
            if (selectedAccessGroup) {
                console.log("Encryption selected for access group: " + encryptionOption, selectedAccessGroup);
            } else {
                console.log("Access group not found for encryption option: " + encryptionOption);
            }
        }
    }, [encryptionOption, accessGroups]);
    


    return (
        <Modal
            show={uploadModal}
            onHide={() => { setUploadModal(false); setUploadFileData(null); }}
            centered
        >
            {uploading ? (
                <Modal.Body className="d-flex flex-column align-items-center justify-content-center p-4">
                    <Spinner />
                    <div className="py-2">Your image is uploading, please wait</div>
                    {progress !== null && (
                        <div className="progress w-75" role="progressbar" style={{ height: `5px` }} aria-label={`Upload progress`} aria-valuenow={progress.toFixed(1)} aria-valuemin="0" aria-valuemax="100">
                            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                        </div>
                    )}
                </Modal.Body>
            ) : (
                <>
                    {uploadError ? (
                        <Modal.Body className="d-flex flex-column align-items-center justify-content-center p-4">
                            <Alert
                                variant="danger"
                                className="w-100 text-center"
                                onClose={() => setUploadError(null)}
                                dismissible
                            >
                                {uploadError}
                            </Alert>
                        </Modal.Body>
                    ) : (
                        <>
                            <Modal.Header className="border-0" closeButton>
                                <Modal.Title>Upload</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                {uploadFileData ? (
                                    <>
                                        {uploadFileData.isImage ? (
                                            <>
                                                <div className="mb-3">
                                                    <div className="fw-bold mb-2">Original Image Preview:</div>
                                                    <img src={URL.createObjectURL(uploadFileData?.blob)} className="img-fluid mb-2" />
                                                    <div>File name: <strong>{uploadFileData.file.name}</strong></div>
                                                    <div>File type: <strong>{uploadFileData.type}</strong></div>
                                                    <div>Extension: <strong>{uploadFileData.extension}</strong></div>
                                                    <div>Dimensions: <strong>{uploadFileData.width} × {uploadFileData.height}px</strong></div>
                                                    <div>File Size: <strong>{formatBytes(uploadFileData.file.size)}</strong></div>
                                                </div>

                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold">Image Format</Form.Label>
                                                    <Form.Select
                                                        value={imageFormat}
                                                        onChange={(e) => setImageFormat(e.target.value)}
                                                    >
                                                        <option value="webp">WEBP</option>
                                                        <option value="jpeg">JPEG</option>
                                                        <option value="png">PNG</option>
                                                        <option value="gif">GIF</option>
                                                    </Form.Select>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold">Resize Option</Form.Label>
                                                    <Form.Select
                                                        value={resizeOption}
                                                        onChange={(e) => setResizeOption(e.target.value)}
                                                    >
                                                        {Object.keys(sizeMap)
                                                            .filter((key) => {
                                                                // If the file is smaller than the 'default', default to 'full', else show smaller options
                                                                const maxWidth = sizeMap[key];
                                                                return maxWidth ? uploadFileData.width > maxWidth : true; // Only show options smaller than current width
                                                            })
                                                            .map((key) => (
                                                                <option key={key} value={key}>
                                                                    {key.charAt(0).toUpperCase() + key.slice(1)}
                                                                    {sizeMap[key] ? ` (max ${sizeMap[key]}px width)` : " (Full Size)"}
                                                                </option>
                                                            ))}
                                                    </Form.Select>
                                                </Form.Group>


                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-bold">Compression Quality: {(compressionQuality * 100).toFixed(0)}%</Form.Label>
                                                    <Form.Range
                                                        min={0.5}
                                                        max={1}
                                                        step={0.05}
                                                        value={compressionQuality}
                                                        onChange={(e) => setCompressionQuality(parseFloat(e.target.value))}
                                                    />
                                                </Form.Group>
                                            </>
                                        ) : (
                                            <>
                                                <div className="fw-bold">File Info:</div>
                                                <div>Name: <strong>{uploadFileData.file.name}</strong></div>
                                                <div>Extension: <strong>{uploadFileData.extension}</strong></div>
                                                <div>Type: <strong>{uploadFileData.type}</strong></div>
                                                <div>Size: <strong>{formatBytes(uploadFileData.file.size)}</strong></div>
                                                
                                            </>
                                        )}

                                        <div className="my-3">
                                            <Form.Label className="fw-bold">Filename:</Form.Label>
                                            <InputGroup className="pb-2">
                                                <Form.Control
                                                    type="text"
                                                    value={filename}
                                                    className={`${filenameError ? 'is-invalid' : ''}`}
                                                    onChange={(handleFilenameChange)}
                                                    placeholder="Enter a custom filename"
                                                    disabled={uploading}
                                                />
                                                <InputGroup.Text className="text-muted">
                                                    .{uploadFileData.extension || imageFormat}
                                                </InputGroup.Text>
                                            </InputGroup>
                                            {filenameError && (
                                                <Alert variant="danger" className="mt-2 py-1 small text-center">
                                                    {filenameError}
                                                </Alert>
                                            )}
                                        </div>

                                        <Form.Group className="my-3">
                                            <Form.Label className="fw-bold">Access Restrictions</Form.Label>
                                            <Form.Select
                                                value={encryptionOption}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setEncryptionOption(val === "null" ? null : val);
                                                  }}
                                            >
                                                <option key={"noencryption"} value={"null"}>
                                                    Public (no encryption) - for use on posts
                                                </option>
                                                <option key={"default-key"} value={"default-key"}>
                                                    Personal access only
                                                </option>
                                                {[...accessGroups.AccessGroupsMember, accessGroups.AccessGroupsOwned].map((group) => (
                                                    <option key={group.AccessGroupPublicKeyBase58Check} value={group.AccessGroupKeyName}>
                                                        {group.ExtraData?.GroupName || group.AccessGroupKeyName}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                        {encryptionOption !== null && (
                                            <>
                                                <Form.Group className="my-3">
                                                    <Form.Label className="fw-bold">Indexing</Form.Label>
                                                    <p className="text-muted small">All information is encrypted - the drawback being that you cannot quickly retrieve files by searching filename or type. You can choose to leave certain file attributes unencrypted to make it easier to index and search.<br/>
                                                    <strong>Note that these parameters will be visible to anyone.</strong></p>
                                                    <Form.Select
                                                        value={indexOption}
                                                        onChange={(e) => setIndexOption(parseInt(e.target.value))}
                                                    >
                                                        <option value={0}>No index (most secure)</option>
                                                        <option value={1}>Index by file type</option>
                                                        <option value={2}>Index by file type and filename</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </>
                                        )}

                                        <div className={`alert alert-${!processing && estimatedCost ? estimatedCost > 1 ? `danger` : estimatedCost > 0.15 ? `warning` : `secondary` : `secondary`} text-center mt-3 mb-0`}>
                                            {processing ? (
                                                <div className="text-center">
                                                    <Spinner size="sm" className='me-2' />
                                                    <div>...estimating storage requirements...</div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div><strong>Estimated Cost:</strong></div>
                                                    <div>~{estimatedCost?.toFixed(4) ?? '...'} DESO (~{localCurrency(estimatedCost, preferences, exchangeRates)})</div>
                                                </>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center">
                                        {processing ? (
                                            <>
                                                <Spinner size="sm" className='me-2' />
                                                <div>...processing...</div>
                                            </>
                                        ) : (
                                            <>
                                                <h5>Choose file to upload</h5>
                                                <p className="text-muted small">
                                                    You can upload any file to the blockchain for storage.<br />
                                                    After selecting, you can review options and estimated fees.
                                                </p>
                                                <input
                                                    type="file"
                                                    hidden
                                                    ref={fileInputRef}
                                                    onChange={handleFileChange}
                                                />
                                                <Button variant="primary" className='me-2' onClick={triggerFileInput} disabled={uploading}>
                                                    {uploading ? <span className="d-flex flex-row align-items-center"><Spinner size="sm" className='me-2' />Uploading...</span> : <><i className="bi bi-upload me-2"></i>Choose file to upload</>}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </Modal.Body>
                            <Modal.Footer className="border-0">
                                <Button variant="secondary" onClick={() => { setUploadModal(false); setUploadFileData(null); }}>
                                    Cancel
                                </Button>
                                {uploadFileData && (
                                    <>
                                        <Button variant="secondary" onClick={() => { setUploadFileData(null); }}>
                                            Reset
                                        </Button>
                                        <Button variant="success" onClick={handleFileUpload} disabled={uploading || filenameError || processing}>
                                            {uploading ? <span className="d-flex flex-row align-items-center"><Spinner size='sm' className='me-2' />uploading...</span> : `Confirm Upload`}
                                        </Button>
                                    </>
                                )}
                            </Modal.Footer>
                        </>
                    )}
                </>
            )}
        </Modal>
    );
};